<template>
  <div class="custom-title">
    <div class="task-table-header" :class="getSortingClass(customField.field_id)">
      <div @click.stop="sortTasks({fieldId: customField.field_id, sortKey: sortEnabledFields[sortEnabledFields.indexOf(customField.field_key)], deliverables: fullDeliverables, namespace:namespace})" :class="!multipleTasks ? 'responsive-labels' : ''" class="field-label cursor-pointer">
        <div v-if="customField.label_id !== null">
          <NewCustomFieldLabel :customField="customField" :taskCount="taskCount" :deliverableId="deliverable.id" :namespace="namespace"/>
        </div>
        <div v-else>
          <span v-if="sortEnabledFields.indexOf(customField.field_key) != -1">
          {{ customField.label }}
            <SortTaskControl />
          </span>
        </div>
      </div>
      <div
        class="last-updated"
        v-if="customField.field_type == 'wysiwyg' && customField.updated_at"
      >Last updated {{ customField.updated_at | moment('MMM D HH:mm a') }}</div>
    </div>
    <div 
      @click="showField" 
      :class="getClasses(customField.field_type)"
      :title="disabled ? 'Read Only' : ''"
    >
      <div class="field-wrap" v-if="customField.field_type == 'wysiwyg'">
        <perfect-scrollbar
          class="exclude-outside-click field-value textarea wysiwyg"
          v-show="!editing"
          v-html="customField.value || 'Click to set...'"
        ></perfect-scrollbar>
        <ckeditor
          editorUrl="/js/ckeditor/ckeditor.js"
          ref="customInput"
          v-show="editing"
          v-click-outside="outside"
          v-model="customField.value"
          :id="'custom-field-'+_uid"
          :config="editorConfig"
          @blur="updateFieldValue(customField)"
        ></ckeditor>
      </div>

      <div class="field-wrap" v-if="customField.field_type == 'textarea'">
        <span
          class="exclude-outside-click field-value text-hover-bg pt-20"
          v-show="!editing"
          :title="customField.value || ''"
          v-b-tooltip.hover
        >{{ customField.value || 'Click to set...' }}</span>
        <div class="custom-field">
          <textarea
            :rows="rows"
            ref="customInput"
            class="form-control custom-inputs"
            v-show="editing"
            v-click-outside="outside"
            @change="updateFieldValue(customField)"
            @blur="editing = false"
            v-model="customField.value"
          />
        </div>
      </div>

      <div 
        class="field-wrap"
        v-if="customField.field_type == 'select'"
      >
        <span
          class="exclude-outside-click field-value text-hover-bg"
          v-show="!editing"
        >{{ customField.value || 'Click to set...' }}</span>
        <div class="custom-field select-dropdown">
          <multiselect
            ref="customInput"
            v-click-outside="outside"
            v-model="customField.value"
            :show-labels="false"
            :options="getOptions"
            :multiple="false"
            v-show="editing"
            @close="editing=false"
            @input="updateFieldValue(customField); editing = false"
          ></multiselect>
        </div>
      </div>

      <div class="field-wrap no-icon" v-if="customField.field_type == 'status'"
        :class="{ 'can-update-task-status': (userAbilities.updateTaskStatus || isTaskAssignedToLoggedInUser) }" >
        <span
          v-if="customField.value"
          class="exclude-outside-click field-value status-field-value"
          :style="{ backgroundColor: parseValue(customField.value).color || customField.value.color }"
        >{{ parseValue(customField.value).name || customField.value.name }}</span>
        <span
          v-if="!customField.value"
          class="exclude-outside-click field-value"
          v-show="!editing"
        >Click to set...</span>
        <div class="custom-field status-field exclude-outside-click">
          <div class="custom-field-outside-bg-mobile" v-show="editing" v-if="false"></div>
          <multiselect
            ref="customInput"
            :searchable="true"
            v-show="editing"
            v-field-dismiss="outside"
            v-model="selectedStatus"
            :options="getOptions"
            @close="editing=false"
            @input="handleFieldValueUpdate"
            :custom-label="customLabel"
            :show-labels="false"
          >
            <template slot="option" slot-scope="props">
              <div
                :style="{ color: 'var(--wf-secondary)', borderRadius: '5px', backgroundColor: props.option.color, height: '40px', lineHeight: '40px', textAlign: 'center' }"
              >{{ props.option.name }}</div>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="field-wrap field-wrap-users no-icon" v-if="customField.field_type == 'users'">
        <div class="field-value exclude-outside-click">
          <span class="info-bubbles" v-if="customField.userName">
            <span
              v-b-tooltip.hover
              :title="customField.userName"
              class="exclude-outside-click initials badge"
              :class="getColorClass()"
              style="display: inline-block"
              @click="checkPosition"
            >{{ customField.userName | textInitials }}</span>
          </span>
          <span class="info-no-user" v-if="!customField.userName && customField.field_id == 14">
          	Creator
          </span>
          <div class="add-multiselect-group">
            <plus-button></plus-button>
            <button @click="deselect(); editing=false"  v-show="editing"></button>
            <div class="custom-field user-field custom-multiselect" v-show="editing">
              <multiselect
                ref="customInput"
                v-click-outside="outside"
                v-model="selectedUser"
                :options="allActiveUsersPlusCreator"
                :option-height="42"
                :show-labels="false"
                :multiple="false"
                label="name"
                :allowEmpty="true"
                track-by="id"
                @close="editing=false"
                @input="updateFieldValue(customField); editing = false"
              ></multiselect>
            </div>
          </div>
        </div>
      </div>

      <div
        ref="refDatepickerWrapper"
        class="field-wrap field-wrap-datepicker exclude-outside-click"
        @click="findPosition()"
        v-if="customField.field_type == 'datepicker'"
      >
        <span v-show="!editing" class="exclude-outside-click field-value text-hover-bg">
          <span
            class="exclude-outside-click"
            v-if="customField.value"
          >{{ customField.value | moment("DD MMM, YYYY") }} </span>
          <span class="exclude-outside-click" v-else>No Deadline</span>
          <i v-if="isDateOverdue(customField)" class="fas fa-exclamation-circle date-overdue-icon" :id="overdueToolTip(customField)"></i>
          <b-tooltip v-if="isDateOverdue(customField)" :target="overdueToolTip(customField)" triggers="hover">
            Overdue!
          </b-tooltip>
          <i v-if="isDateToday(customField)" class="fas fa-calendar date-today-icon" :id="overdueToolTip(customField)"></i>
          <b-tooltip v-if="isDateToday(customField)" :target="overdueToolTip(customField)" triggers="hover">
            Due today!
          </b-tooltip>
        </span>
        <div v-show="editing" class="custom-field date-pick-field">
          <div class="custom-field-outside-bg-mobile" v-if="false"></div>
          <vuejs-datepicker
            ref="customDatepicker"
            :format="customDateFormat"
            v-field-dismiss="outside"
            name="due_date"
            v-show="editing"
            v-model="customField.value"
            @input="updateFieldValue(customField)"
            @selected="dateSelected=true"
            input-class="custom-datepicker form-control"
            :inline="true"
          >
            <div slot="beforeCalendarHeader" class="calender-header">
              <b-button class="remove-dl-btn" variant="text" @click="resetDeadline">Remove Deadline</b-button>
              <b-button class="today-btn" variant="text" @click="onToday">Today</b-button>
            </div>

          </vuejs-datepicker>
        </div>
      </div>

      <span v-if="customField.field_type == 'file'">
        <input type="file" />
      </span>

      <span v-if="customField.field_type == 'radio'">
        <label
          class="ml-2"
          v-for="(radioValue, index) in parseValue(customField.value_set)"
          :key="'radio'+index"
        >
          <input
            @change="updateFieldValue(customField)"
            type="radio"
            :value="radioValue"
            v-model="customField.value"
          />
          {{ radioValue }}
        </label>
      </span>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { mapActions, mapState } from "vuex";
import router from '../../routes';
import NewCustomFieldLabel from './NewCustomFieldLabel.vue';
import SortTaskControl from './SortTaskControl';
export default {
  name: "CustomFields",
  props: {
    fullDeliverables: '',
    namespace: String,
    sortEnabledFields: Array,
    customField: Object,
    task:Object,
    users: Array,
    showLabel: {
      type: Boolean,
      default: true
    },
    isTaskAssignedToLoggedInUser: {
      type: Boolean,
      default: false
    },
    multipleTasks: {
      type: Boolean,
      default: true
    },
    taskCount: {
      type: Number,
      default: 1
    },
    rows: {
      type: Number,
      default: 10
    },
    deliverable: Object,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editing: false,
      dateSelected: false,
      customToolbar: [
        ["bold", "italic", "underline", "link"],
        [{ list: "ordered" }, { list: "bullet" }]
      ],
      editorConfig: {
        toolbar:  [
          { name: 'document', groups: [ 'mode' ], items: [ 'Source' ] },
          { name: 'clipboard', groups: [ 'clipboard', 'undo' ], items: [ 'Cut', 'Copy', 'Paste', 'PasteText', '-', 'Undo', 'Redo' ] },
          { name: 'basicstyles', groups: [ 'basicstyles', 'cleanup' ], items: [ 'Bold', 'Italic', 'Underline', '-', 'RemoveFormat' ] },
          { name: 'paragraph', groups: [ 'list', 'blocks', 'align' ], items: [ 'NumberedList', 'BulletedList', 'Blockquote', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock' ] },
          { name: 'links', items: [ 'Link', 'Unlink' ] },
          { name: 'insert', items: [ 'Image', 'Table', 'HorizontalRule' ] },
          { name: 'styles', items: [ 'Styles' ] },
          { name: 'colors', items: [ 'TextColor', 'BGColor' ] },
          { name: 'tools', items: [ 'Maximize' ] }
        ],
      },
      previousValue: null,
    };
  },
  computed: {
  	...mapState({
  	  state (state) {
  	    return state[this.namespace]
  	  },
  	  allUsers (state, getters) {
  	    return getters['allUsers']
  	  },
  	  userAbilities (state, getters) {
  	    return getters['userAbilities']
  	  },
  	  allActiveUsers (state, getters) {
  	    return getters['allActiveUsers']
  	  },
  	  allActiveUsersPlusCreator (state, getters) {
  	    const data = _.cloneDeep(getters['allActiveUsers']);
  	    var newData = [];
  	    var creator = {} ;
  	    creator.id = null;
  	    creator.name = "Deliverable Creator";
  	    newData.push(creator);
  	    for (var x in data) {
  	    	newData.push(data[x]);
  	    }

  	    return newData;
  	  },
  	}),
    _: function() {
      return _;
    },
    selectedUser: {
      get() {
        return this.allUsers.find(user => user.id == this.customField.value);
      },
      set(newValue) {
        if (newValue == null) {
          return;
        }
        this.customField.userName = newValue.name;
        this.customField.value = newValue.id;
      }
    },
    selectedStatus: {
      get() {
        return this.customField.value ? this.customField.value : '';
      },
      set(newValue) {
        this.previousValue = this.customField.value;
        this.customField.value = newValue;
      }
    },
    getOptions() {
      if (this.customField) {
        if (this.customField.value_set) {
          let parsed = this.parseValue(this.customField.value_set);
          if (parsed == false) {
            return []; // if we don't check for false... it thows up errors
          } else {
            // build value and text
            return parsed;
          }
        }
      }
      return [];
    }
  },
  components: {
    SortTaskControl,
    "vuejs-datepicker": Datepicker,
    NewCustomFieldLabel
  },
  methods: {
    ...mapActions({
      updateFieldValue (dispatch, payload) {
        return dispatch(this.namespace + 'updateFieldValue', payload)
      },
    }),
    async handleFieldValueUpdate(value) {
      this.editing = false;
      try {
        const payload = {
          ...this.customField,
          value 
        }
        const response = await this.updateFieldValue(payload);
      } catch (error) {
        this.customField.value = this.previousValue;
      }
    },


    isDateToday(customField){
      let isToday = false;
    if(customField.value){
	      if (this.task) {
	      	if (customField.field_key == "due_date") {
	      		// find the task status field, and if it isn't completed or cancelled, show the alert.
	      		for (var x = 0; x < this.task.taskFields.length; x++) {
	      			if (this.task.taskFields[x].field_key == "status") {
	      				var obj = this.task.taskFields[x].value;
	      				if (typeof this.task.taskFields[x].value != "object") {
			      			var val = JSON.parse(this.task.taskFields[x].value);
			      			obj = val;
			      		}
			      		if (typeof obj.slug != "undefined") {
				      		if (obj.slug != "completed" && obj.slug != "cancelled") {
						        let testDate = this.$moment.utc(customField.value).local();
						        let now = this.$moment.utc().local();
					          if(testDate.isSame(now, 'day')){
					            isToday = true;
					          }
						       }
					     }
	      			}
	      		}
	      	}
	      }
	    }
      return isToday;
    },
    isDateOverdue(customField){
      let isOverdue = false;
      if(customField.value){
	      if (this.task) {
	      	if (customField.field_key == "due_date") {
	      		// find the task status field, and if it isn't completed or cancelled, show the alert.
	      		for (var x = 0; x < this.task.taskFields.length; x++) {
	      			if (this.task.taskFields[x].field_key == "status") {
	      				var obj = this.task.taskFields[x].value;
	      				if (typeof this.task.taskFields[x].value != "object") {
			      			var val = JSON.parse(this.task.taskFields[x].value);
			      			obj = val;
			      		}
			      		if (typeof obj.slug != "undefined") {
				      		if (obj.slug != "completed" && obj.slug != "cancelled") {
						        let testDate = this.$moment.utc(customField.value).local();
						        let now = this.$moment.utc().local();
					          if(testDate.isBefore(now, 'day')){
					            isOverdue = true;
					          }
						       }
					     }
	      			}
	      		}
	      	}
	      }
	    }
      return isOverdue;
    },
    overdueToolTip(customField){
      return "overdue-date-tooltip-"+customField.id;
    },


    customLabel({ name }) {
      return name;
    },
    getClasses(field_type) {
      let classes = "field-outer-wrap field-wrap"; // Not sure why we are wrapping the field with 2 field-wrap classes. Makes targeting confusing imo. But don't have time to evaluate impact of removing it - Phil 2025-07-08
      if (field_type == "status") {
        classes = "";
      }
      classes += this.disabled ? ' disabled' : '';
      return classes;
    },
    getSortingClass(fieldId) {
      let currentSortFieldId = localStorage.getItem('storedSortFieldId')
      let currentSortState = localStorage.getItem('storedSortState')
      return fieldId == currentSortFieldId ? `sort-active ${currentSortState}-${currentSortFieldId}` : ''
    },
    customDateFormat(date) {
      return this.$moment(date, "YYYY-MM-DD").format("DD MMM, YYYY");
    },
    onToday() {
      this.customField.value = this.$moment().format("YYYY-MM-DD H:m:s");
      this.editing = false
      this.dateSelected = true
      this.updateFieldValue(this.customField);
    },
    resetDeadline() {
      this.customField.value = null;
      this.editing = false
      this.dateSelected = true
      this.updateFieldValue(this.customField);
    },

    parseValue(str) {
      try {
        return JSON.parse(str);
      } catch (e) {
        return false;
      }
    },
    findPosition() {
      if (this.$refs.refDatepickerWrapper.getBoundingClientRect().y > 315) {
        $(".vdp-datepicker__calendar").css({"top": "-302px", "position": "absolute"});
      } else {
        $(".vdp-datepicker__calendar").css("top", "unset");
      }
    },
    showField() {
      
      if(this.disabled) { return false; }

      if (
        this.editing == false &&
        this.customField.field_type == 'status') {
        /*
         * Only allow updating task status if user has edit media call / deliverable permission or 
         * Update Task Status permission, or 
         * if the person logged in is assigned to the task.
         */
        if (this.userAbilities) {
          if (
            this.userAbilities.editMediaCall == false &&
            this.userAbilities.updateTaskStatus == false &&
            this.isTaskAssignedToLoggedInUser == false
          ) {
            return;
          }
        }
      } else {
        if (typeof this.userAbilities.editMediaCall == 'undefined' || !this.userAbilities.editMediaCall || this.deliverable.permissions.viewOnly) return;
      }
      if (this.editing && this.customField.field_type == 'status') {
        this.editing = false;
        return;
      }
      if (this.editing && !this.dateSelected) return;
      this.editing = true;
      this.$nextTick(() => {
        if (typeof this.$refs.customInput != "undefined") {
          if (typeof this.$refs.customInput.quill != "undefined") {
            this.$refs.customInput.quill.focus();
          } else if (typeof this.$refs.customInput.$el != "undefined") {
            this.$refs.customInput.$el.focus();
          } else {
            this.$refs.customInput.focus();
          }
        } else if (typeof this.$refs.customDatepicker != "undefined") {
          if (!this.dateSelected) {
            this.$refs.customDatepicker.showCalendar();
            this.$refs.customDatepicker.$el.querySelector("input").focus();
          } else if (this.dateSelected) {
            this.dateSelected = false;
            this.editing = false;
          }
        }
      });
    },
    getColorClass() {
      if(router.currentRoute.name == 'myWeek') {
        return this.customField.value == localStorage.getItem('userId') ? 'badge-primary' : 'other-user-badge'
      } else {
        return 'badge-primary'
      }
    },
    deselect() {
      this.customField.value = null;
      this.updateFieldValue(this.customField);
    },
    getStatuses(dataJson) {
      let data = this.parseValue(dataJson),
        options = [];
      data.forEach(status => {
        options.push(status.name);
      });
      return options;
    },
    outside() {
      this.editing = false;
      this.dateSelected = false;
    },
    checkPosition(e) {
      setTimeout(function() {
        if($(e.target).parents('.field-value').find('.multiselect--above').length) {
          $('.user-field').addClass('has__above__multiselect');
        } else {
          $('.user-field').removeClass('has__above__multiselect');
        }
      }, 10);
      return;
    }
  },
  mounted() {
    let self = this;

    if(this.isMobile()) {
      $('.custom-datepicker').on('blur', function() {
        $(this).parents('.vdp-datepicker').hide();
      })
    }
    window.addEventListener("keyup", function(e) {
      if (e.keyCode === 27) {
        self.editing = false;
        self.dateSelected = false;
      }
    });
  }
};
</script>

<style>
</style>
